import { useAuth } from 'context/useAuth';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  doc,
  documentId,
  endAt,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  QueryConstraint,
  QueryDocumentSnapshot,
  startAfter,
  startAt,
} from 'firebase/firestore';
import { Installation } from 'types/Installation';

export type InstallationSortLabel = 'Latest' | 'A - Z' | 'Z - A';

interface UseInstallationsSearchOptions {
  query?: string;
  sort?: InstallationSortLabel;
  pageSize?: number;
}

interface UseInstallationsSearchResult {
  installations: Installation[];
  loading: boolean;
  error: string;
  pageIndex: number;
  hasNext: boolean;
  hasPrev: boolean;
  next: () => void;
  prev: () => void;
}

function normaliseToken(input: string): string {
  return (input || '').trim().toLocaleLowerCase();
}

// Max docs we will pull when running a client-side substring search. The
// installations collection has no `search.keywords` denormalisation yet, so
// id / deviceId / installer-name lookups must be done in-memory. Keep this
// high enough to cover realistic tenants but bounded to avoid a runaway read.
const CLIENT_SEARCH_FETCH_LIMIT = 2000;

function buildHaystack(data: Installation, docId: string): string {
  const deviceId = data.deviceId || '';
  const createdBy = data.audit?.createdBy || '';
  return `${docId}~${deviceId}~${createdBy}`.toLocaleLowerCase();
}

export default function useInstallationsSearch(
  options: UseInstallationsSearchOptions = {}
): UseInstallationsSearchResult {
  const { query: rawQuery = '', sort = 'Latest', pageSize = 10 } = options;
  const { firestore } = useAuth();

  const [installations, setInstallations] = useState<Installation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [hasNext, setHasNext] = useState(false);

  // Server-side cursor pagination (used when there is no search query).
  const cursorsRef = useRef<Array<QueryDocumentSnapshot | null>>([null]);
  const lastDocRef = useRef<QueryDocumentSnapshot | null>(null);

  // Client-side filtered cache (used when there IS a search query).
  const filteredRef = useRef<Installation[]>([]);

  // Tracks whether the component is still mounted, and which fetch is the
  // latest in flight. Older responses are dropped to prevent races.
  const mountedRef = useRef(true);
  const requestIdRef = useRef(0);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const normalisedQuery = useMemo(() => normaliseToken(rawQuery), [rawQuery]);

  // Server-side paginated fetch — only used when no search query is active.
  const fetchServerPage = useCallback(
    async (targetPageIndex: number) => {
      if (!firestore) return;
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setError('');

      try {
        const constraints: QueryConstraint[] = [];

        if (sort === 'A - Z') {
          constraints.push(orderBy('__name__', 'asc'));
        } else if (sort === 'Z - A') {
          constraints.push(orderBy('__name__', 'desc'));
        } else {
          // 'Latest' — installation ids are time-encoded (epochMillis prefix
          // base36-encoded), so a __name__ desc sort reliably surfaces the
          // newest installations first and includes records that may not
          // have audit.createdAt populated.
          constraints.push(orderBy('__name__', 'desc'));
        }

        const cursor = cursorsRef.current[targetPageIndex] ?? null;
        if (cursor) {
          constraints.push(startAfter(cursor));
        }

        constraints.push(limit(pageSize + 1));

        const q = query(collection(firestore, 'installations'), ...constraints);
        const snap = await getDocs(q);

        if (!mountedRef.current || requestId !== requestIdRef.current) return;

        const docs = snap.docs;
        const pageDocs = docs.slice(0, pageSize);
        const hasNextPage = docs.length > pageSize;

        setInstallations(
          pageDocs.map((d) => {
            const data = d.data() as Installation;
            (data as any).id = d.id;
            return data;
          })
        );
        setHasNext(hasNextPage);
        setPageIndex(targetPageIndex);
        lastDocRef.current = pageDocs[pageDocs.length - 1] ?? null;
      } catch (err: any) {
        if (!mountedRef.current || requestId !== requestIdRef.current) return;
        setError(err?.message || String(err));
        setInstallations([]);
        setHasNext(false);
      } finally {
        if (mountedRef.current && requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [firestore, sort, pageSize]
  );

  // Slice the in-memory filtered list to the requested page.
  const showFilteredPage = useCallback(
    (targetPageIndex: number) => {
      const all = filteredRef.current;
      const start = targetPageIndex * pageSize;
      const end = start + pageSize;
      setInstallations(all.slice(start, end));
      setHasNext(end < all.length);
      setPageIndex(targetPageIndex);
    },
    [pageSize]
  );

  // Client-side search: fetch (up to CLIENT_SEARCH_FETCH_LIMIT) and filter
  // on id / deviceId / installer name (audit.createdBy) — same fields the
  // legacy useGetInstallations + AllInstallations.filter combo matched on.
  //
  // We ALSO run two targeted lookups so a known id is always findable even
  // if the recency / alphabetical scan window wouldn't otherwise reach it:
  //   1. Exact doc-id getDoc(rawQuery)
  //   2. Doc-id prefix range using documentId() startAt..endAt
  // Results from all three sources are merged & deduped by id.
  const runClientSearch = useCallback(async () => {
    if (!firestore || !normalisedQuery) return;
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError('');

    try {
      const rawTrim = (rawQuery || '').trim();
      const matchesById = new Map<string, Installation>();

      const addDoc = (d: { id: string; data: () => any }) => {
        if (matchesById.has(d.id)) return;
        const data = d.data() as Installation;
        (data as any).id = d.id;
        matchesById.set(d.id, data);
      };

      // 1. Exact doc-id hit.
      if (rawTrim) {
        try {
          const exact = await getDoc(doc(firestore, 'installations', rawTrim));
          if (exact.exists()) addDoc(exact);
        } catch {
          // ignore
        }
      }

      // 2. Doc-id prefix range.
      if (rawTrim) {
        try {
          const prefixQ = query(
            collection(firestore, 'installations'),
            orderBy(documentId()),
            startAt(rawTrim),
            endAt(rawTrim + '\uf8ff'),
            limit(CLIENT_SEARCH_FETCH_LIMIT),
          );
          const prefixSnap = await getDocs(prefixQ);
          for (const d of prefixSnap.docs) addDoc(d);
        } catch {
          // ignore
        }
      }

      // 3. Bounded scan for deviceId / createdBy / substring id hits.
      const constraints: QueryConstraint[] = [];
      if (sort === 'A - Z') {
        constraints.push(orderBy('__name__', 'asc'));
      } else if (sort === 'Z - A') {
        constraints.push(orderBy('__name__', 'desc'));
      } else {
        // 'Latest' — see fetchServerPage for rationale.
        constraints.push(orderBy('__name__', 'desc'));
      }
      constraints.push(limit(CLIENT_SEARCH_FETCH_LIMIT));

      const q = query(collection(firestore, 'installations'), ...constraints);
      const snap = await getDocs(q);

      if (!mountedRef.current || requestId !== requestIdRef.current) return;

      for (const d of snap.docs) {
        if (matchesById.has(d.id)) continue;
        const data = d.data() as Installation;
        (data as any).id = d.id;
        if (buildHaystack(data, d.id).indexOf(normalisedQuery) > -1) {
          matchesById.set(d.id, data);
        }
      }

      const matches = Array.from(matchesById.values());

      filteredRef.current = matches;
      const start = 0;
      const end = pageSize;
      setInstallations(matches.slice(start, end));
      setHasNext(matches.length > pageSize);
      setPageIndex(0);
    } catch (err: any) {
      if (!mountedRef.current || requestId !== requestIdRef.current) return;
      setError(err?.message || String(err));
      filteredRef.current = [];
      setInstallations([]);
      setHasNext(false);
    } finally {
      if (mountedRef.current && requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [firestore, normalisedQuery, rawQuery, sort, pageSize]);

  useEffect(() => {
    cursorsRef.current = [null];
    lastDocRef.current = null;
    filteredRef.current = [];
    if (normalisedQuery) {
      runClientSearch();
    } else {
      fetchServerPage(0);
    }
  }, [normalisedQuery, runClientSearch, fetchServerPage]);

  const next = useCallback(() => {
    if (!hasNext || loading) return;
    const nextIndex = pageIndex + 1;
    if (normalisedQuery) {
      showFilteredPage(nextIndex);
      return;
    }
    if (cursorsRef.current.length <= nextIndex) {
      cursorsRef.current[nextIndex] = lastDocRef.current;
    }
    fetchServerPage(nextIndex);
  }, [hasNext, loading, pageIndex, normalisedQuery, showFilteredPage, fetchServerPage]);

  const prev = useCallback(() => {
    if (pageIndex <= 0 || loading) return;
    if (normalisedQuery) {
      showFilteredPage(pageIndex - 1);
      return;
    }
    fetchServerPage(pageIndex - 1);
  }, [pageIndex, loading, normalisedQuery, showFilteredPage, fetchServerPage]);

  return {
    installations,
    loading,
    error,
    pageIndex,
    hasNext,
    hasPrev: pageIndex > 0,
    next,
    prev,
  };
}
